<script>
import { ref } from 'vue';
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LabelTagIcon from 'dashboard/components-next/label/LabelTagIcon.vue';
import FunilLabelCreateModal from 'dashboard/components/widgets/conversation/FunilLabelCreateModal.vue';
import FunilLabelDeleteModal from 'dashboard/components/widgets/conversation/FunilLabelDeleteModal.vue';
import { fetchManagedFunilLabels } from 'dashboard/api/aceleres/funilLabels';
import { useAlert } from 'dashboard/composables';

export default {
  components: {
    Spinner,
    LabelDropdown,
    Button,
    LabelTagIcon,
    FunilLabelCreateModal,
    FunilLabelDeleteModal,
  },
  setup() {
    const { isAdmin } = useAdmin();

    const {
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
    } = useConversationLabels();

    const showSearchDropdownLabel = ref(false);

    const toggleLabels = () => {
      showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
    };

    const closeDropdownLabel = () => {
      showSearchDropdownLabel.value = false;
    };

    const keyboardEvents = {
      KeyL: {
        action: e => {
          e.preventDefault();
          toggleLabels();
        },
      },
      Escape: {
        action: () => {
          if (showSearchDropdownLabel.value) {
            toggleLabels();
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);
    return {
      isAdmin,
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
      showSearchDropdownLabel,
      closeDropdownLabel,
      toggleLabels,
    };
  },
  data() {
    return {
      selectedLabels: [],
      showCreateModal: false,
      createPrefill: '',
      // Titulos que esta funcionalidade criou — so neles aparece o caixote.
      deletableTitles: [],
      labelToDelete: null,
    };
  },

  computed: {
    ...mapGetters({
      conversationUiFlags: 'conversationLabels/getUIFlags',
    }),
  },
  watch: {
    // Ao abrir o seletor. Fechar nao busca nada.
    showSearchDropdownLabel(aberto) {
      if (aberto) this.loadDeletableTitles();
    },
  },

  methods: {
    /**
     * Aceleres: a criacao abre um modal CENTRADO NA TELA.
     *
     * O modal nao pode nascer dentro do seletor: aquele container tem
     * `backdrop-blur`, e backdrop-filter cria bloco de contencao — um
     * `position: fixed` la dentro passa a se posicionar pelo painel do
     * contato em vez da janela, e o modal aparecia colado em cima da ficha.
     * Por isso ele mora aqui fora, e ainda vai num <Teleport> para o body.
     */
    onRequestCreate(nome) {
      this.createPrefill = nome || '';
      this.showCreateModal = true;
      this.closeDropdownLabel();
    },

    onLabelCreated(label) {
      this.showCreateModal = false;
      this.addLabelToConversation(label);
      this.loadDeletableTitles();
    },

    /**
     * Busca ao ABRIR o seletor, nao ao montar a tela: a lista muda quando se
     * cria ou apaga etiqueta, e o seletor abre poucas vezes. Buscar aqui evita
     * uma chamada por cartao da lista de conversas e mantem o dado fresco.
     */
    async loadDeletableTitles() {
      if (!this.isAdmin) return;

      try {
        this.deletableTitles = await fetchManagedFunilLabels();
      } catch (error) {
        // Sem a lista ninguem fica com o caixote — melhor do que oferecer
        // apagar uma etiqueta que talvez nao possa ser apagada.
        this.deletableTitles = [];
      }
    },

    onRequestDelete(label) {
      this.labelToDelete = label;
      this.closeDropdownLabel();
    },

    onLabelDeleted({ title, conversations }) {
      this.labelToDelete = null;
      this.deletableTitles = this.deletableTitles.filter(t => t !== title);
      this.$store.dispatch('labels/get');
      // A etiqueta apagada pode estar na conversa aberta; sem isto o chip dela
      // ficaria na tela ate a proxima carga.
      const conversationId = this.$store.getters.getSelectedChat?.id;
      if (conversationId) {
        this.$store.dispatch('conversationLabels/get', conversationId);
      }
      useAlert(
        this.$t('CONTACT_PANEL.LABELS.LABEL_SELECT.DELETED', { name: title }) +
          (conversations > 0 ? ` (${conversations})` : '')
      );
    },
  },
};
</script>

<template>
  <div class="sidebar-labels-wrap">
    <div
      v-if="!conversationUiFlags.isFetching"
      class="contact-conversation--list"
    >
      <div
        v-on-clickaway="closeDropdownLabel"
        class="label-wrap flex flex-wrap"
        @keyup.esc="closeDropdownLabel"
      >
        <div class="conversation-labels-controls">
          <button
            type="button"
            class="conversation-labels-picker"
            @click="toggleLabels"
          >
            <span class="conversation-labels-picker__placeholder">
              {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.TITLE') }}...
            </span>
            <span
              class="conversation-labels-picker__icon i-lucide-chevron-down"
              aria-hidden="true"
            />
          </button>
          <Button
            faded
            xs
            icon="i-lucide-plus"
            class="conversation-labels-add-button"
            :label="
              $t('CONTACT_PANEL.LABELS.CONVERSATION.ADD_BUTTON').split(' ')[0]
            "
            @click="toggleLabels"
          />

          <div
            :class="{
              'block visible': showSearchDropdownLabel,
              'hidden invisible': !showSearchDropdownLabel,
            }"
            class="conversation-labels-dropdown border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] absolute w-full shadow-lg border-n-strong dark:border-n-strong p-2 box-border z-[9999]"
          >
            <LabelDropdown
              v-if="showSearchDropdownLabel"
              :account-labels="accountLabels"
              :selected-labels="savedLabels"
              :allow-creation="isAdmin"
              funil-creation
              :deletable-titles="deletableTitles"
              @add="addLabelToConversation"
              @remove="removeLabelFromConversation"
              @create="onRequestCreate"
              @delete="onRequestDelete"
            />
          </div>
        </div>
        <div
          v-if="activeLabels.length"
          class="conversation-labels-active !gap-2 mt-3"
        >
          <div
            v-for="label in activeLabels"
            :key="label.id"
            class="inline-flex h-8 max-w-full min-w-0 items-center gap-1.5 rounded-full border border-n-weak bg-white py-1 pl-2.5 pr-1.5 text-[13px] font-medium text-n-slate-12 shadow-sm"
          >
            <LabelTagIcon :color="label.color" />
            <span class="min-w-0 truncate">{{ label.title }}</span>
            <button
              type="button"
              class="flex size-5 shrink-0 items-center justify-center rounded-full text-n-slate-11 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :aria-label="`Remover etiqueta ${label.title}`"
              @click.stop="removeLabelFromConversation(label.title)"
            >
              <span class="text-lg leading-none" aria-hidden="true">×</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <Spinner v-else />

    <Teleport to="body">
      <woot-modal
        v-model:show="showCreateModal"
        :on-close="() => (showCreateModal = false)"
      >
        <FunilLabelCreateModal
          :account-labels="accountLabels"
          :prefill="createPrefill"
          @created="onLabelCreated"
          @close="showCreateModal = false"
        />
      </woot-modal>

      <woot-modal
        v-if="labelToDelete"
        show
        :on-close="() => (labelToDelete = null)"
      >
        <FunilLabelDeleteModal
          :label="labelToDelete"
          @deleted="onLabelDeleted"
          @close="labelToDelete = null"
        />
      </woot-modal>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-labels-wrap {
  margin-bottom: 0;
}
.contact-conversation--list {
  width: 100%;
  border-radius: 10px;

  .label-wrap {
    line-height: 1.5rem;
    position: relative;
    align-items: flex-start;
  }
}

.conversation-labels-controls {
  position: relative;
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: stretch;
  gap: 1.5rem;
}

.conversation-labels-active {
  display: flex;
  min-width: 0;
  flex: 1 1 100%;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0;
}

.conversation-labels-dropdown {
  top: calc(100% + 0.5rem);
  left: 0;
}

.conversation-labels-picker {
  display: inline-flex;
  min-width: 0;
  min-height: 2.75rem;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex: 1 1 auto;
  padding: 0 1rem;
  border: 1px solid #dfe3e8;
  border-radius: 0.75rem;
  background: #fff;
  color: #a1a8b0;
  cursor: pointer;
  font-size: 1rem;
  text-align: left;
}

.conversation-labels-picker:hover {
  border-color: #cbd5e1;
}

.conversation-labels-picker__placeholder {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conversation-labels-picker__icon {
  width: 1.25rem;
  height: 1.25rem;
  flex: 0 0 1.25rem;
  color: #7b8490;
}

.conversation-labels-add-button {
  min-height: 2.75rem;
  flex: 0 0 auto;
  border: 1px solid #dfe3e8 !important;
  border-radius: 0.75rem !important;
  background: #fff !important;
  color: #278c68 !important;
  font-weight: 600 !important;
}

@media (max-width: 640px) {
  .conversation-labels-controls {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .conversation-labels-picker {
    flex: 1 1 100%;
  }

  .conversation-labels-add-button {
    width: 100%;
  }
}
</style>
